import { Directive, Input, OnChanges, SimpleChanges, TemplateRef, ViewContainerRef } from '@angular/core';
import { PermissionsService } from 'src/app/components/services/permissions.service';
import { AuthenticationService } from 'src/app/components/services/auth/authentication.service';
import { ResolvedPermission } from 'src/app/components/auth/auth.models';

/**
 * Directiva estructural que oculta el elemento si el usuario no tiene
 * el permiso requerido.
 *
 * Uso básico (verifica can_view):
 *   <div *hasPermission="'reportes.turnados'">...</div>
 *
 * Uso con acción específica:
 *   <button *hasPermission="'reportes.turnados'; hasPermissionAction: 'can_edit'">Editar</button>
 *
 * Bypass automático:
 *   - Si el rol legacy es 'superadmin' o 'admin' → muestra todo.
 *   - Si no hay permisos cargados en sesión → muestra todo (compatibilidad).
 */
@Directive({ selector: '[hasPermission]' })
export class HasPermissionDirective implements OnChanges {

  @Input('hasPermission') menuItemKey: string;
  /** Alias explícito para microsyntax Angular: *hasPermission="'key'; hasPermissionAction: 'can_create'" */
  @Input('hasPermissionAction') action: keyof ResolvedPermission = 'can_view';
  @Input('hasPermissionElse') elseTemplate: TemplateRef<any>;

  private isVisible = false;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permissionsService: PermissionsService,
    private authService: AuthenticationService,
  ) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.updateView();
  }

  private show(): void {
    if (!this.isVisible) {
      this.isVisible = true;
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.templateRef);
    }
  }

  private hide(): void {
    this.isVisible = false;
    this.viewContainer.clear();
    if (this.elseTemplate) {
      this.viewContainer.createEmbeddedView(this.elseTemplate);
    }
  }

  private updateView(): void {
    if (!this.menuItemKey) {
      this.hide();
      return;
    }

    const role = this.authService.currentUserValue?.roles;

    // Bypass: roles de acceso total (legacy)
    if (role === 'superadmin' || role === 'admin') {
      this.show();
      return;
    }

    const perms = this.permissionsService.permissions;

    // Bypass: sin permisos cargados → mostrar (sesión anterior al sistema)
    if (!perms || Object.keys(perms).length === 0) {
      this.show();
      return;
    }

    if (this.permissionsService.can(this.menuItemKey, this.action)) {
      this.show();
    } else {
      this.hide();
    }
  }
}
